import { ArrowLeft, Check, Loader2, MessageSquare, Send } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { VoiceInputButton } from "@/components/shared/VoiceInputButton";
import { useOperations } from "@/context/OperationContext";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { sowiftService } from "@/lib/services";
import type { SowiftAnalysisMessage, SowiftAnalysisSessionResponse } from "@/types";

export function InterviewChat({
  projectId,
  session,
  onBack,
  onSessionUpdate,
}: Readonly<{
  projectId: string;
  session: SowiftAnalysisSessionResponse;
  onBack: () => void;
  onSessionUpdate: (s: SowiftAnalysisSessionResponse) => void;
}>) {
  const { trackOperation } = useOperations();
  const [messages, setMessages] = useState<SowiftAnalysisMessage[]>(session.messages || []);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [isComplete, setIsComplete] = useState(session.status === "completed");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Operation stream
  const [operationId, setOperationId] = useState<string | null>(null);
  const fullResponseRef = useRef("");
  const sentMessageRef = useRef<SowiftAnalysisMessage | null>(null);

  const handleInterviewEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "content_delta": {
          const delta = (data as { delta: string }).delta;
          fullResponseRef.current += delta;
          setStreamingContent(fullResponseRef.current);
          break;
        }
        case "message_end": {
          const complete = (data as { complete: boolean }).complete;
          if (complete) {
            setIsComplete(true);
          }
          break;
        }
        case "operation_completed": {
          const cleanContent = fullResponseRef.current.replace(/\[INTERVIEW_COMPLETE\]/g, "").trim();
          const assistantMsg: SowiftAnalysisMessage = { role: "assistant", content: cleanContent };
          setMessages((prev) => [...prev, assistantMsg]);
          setStreamingContent("");
          setStreaming(false);
          setOperationId(null);

          // Update parent session state
          if (sentMessageRef.current) {
            onSessionUpdate({
              ...session,
              messages: [...messages, sentMessageRef.current, assistantMsg],
              status: isComplete ? "completed" : session.status,
            });
            sentMessageRef.current = null;
          }
          fullResponseRef.current = "";
          break;
        }
        case "operation_failed": {
          const errorData = data as { error?: string };
          console.error("Interview stream error:", errorData.error);
          setStreaming(false);
          setOperationId(null);
          fullResponseRef.current = "";
          break;
        }
        case "error": {
          console.error("Interview stream error:", data);
          break;
        }
      }
    },
    [session, messages, isComplete, onSessionUpdate],
  );

  useOperationStream(operationId, {
    onEvent: handleInterviewEvent,
    onError: (err) => {
      console.error("Interview operation stream error:", err);
      setStreaming(false);
      setOperationId(null);
      fullResponseRef.current = "";
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || streaming || isComplete) return;

    const userMsg: SowiftAnalysisMessage = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setStreaming(true);
    setStreamingContent("");
    fullResponseRef.current = "";
    sentMessageRef.current = userMsg;

    try {
      const result = await sowiftService.sendInterviewMessage(projectId, session.uuid, { content: trimmed });
      setOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "sowift_interview",
        entity_id: session.uuid,
        status: "running",
        title: "SoWift interview",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });
    } catch (err) {
      console.error("Failed to send interview message:", err);
      setStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full max-w-3xl mx-auto -mt-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-content-tertiary hover:text-content-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Analysis
        </button>
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-brand-secondary" />
          <span className="text-sm font-medium text-content-primary">AI Interview</span>
          {isComplete && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
              Complete
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto space-y-4 mb-4 min-h-0">
        {messages.map((msg, i) => (
          <div
            key={`interview-${msg.role}-${i}`}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-xl px-4 py-3 ${
                msg.role === "user" ? "bg-brand-primary-subtle text-white" : "bg-surface-secondary text-content-primary"
              }`}
            >
              <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
            </div>
          </div>
        ))}

        {/* Streaming message */}
        {streaming && streamingContent && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-xl px-4 py-3 bg-surface-secondary text-content-primary">
              <div className="text-sm whitespace-pre-wrap">{streamingContent}</div>
            </div>
          </div>
        )}

        {/* Streaming indicator */}
        {streaming && !streamingContent && (
          <div className="flex justify-start">
            <div className="rounded-xl px-4 py-3 bg-surface-secondary">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-brand-secondary animate-spin" />
                <span className="text-sm text-content-tertiary">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {isComplete ? (
        <div className="shrink-0 border-t border-border-primary pt-4">
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <Check className="h-4 w-4 text-green-400" />
            <p className="text-sm text-green-400">
              Interview complete. The AI has gathered enough information. You can now generate your Statement of Work
              from the Documents tab.
            </p>
          </div>
        </div>
      ) : (
        <div className="shrink-0 border-t border-border-primary pt-4">
          <div className="flex gap-3">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your response..."
              rows={2}
              disabled={streaming}
              className="flex-1 px-4 py-3 bg-surface-secondary border border-border-secondary rounded-xl text-content-primary placeholder:text-content-quaternary text-sm focus:outline-hidden focus:ring-2 focus:ring-brand-secondary/50 resize-none disabled:opacity-50"
            />
            <div className="flex flex-col gap-2 self-end">
              <VoiceInputButton
                onTranscript={(text) => setInput((prev) => (prev ? `${prev} ${text}` : text))}
                disabled={streaming}
              />
              <button
                onClick={handleSend}
                disabled={streaming || !input.trim()}
                className="shrink-0 p-3 bg-brand-gradient rounded-xl text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <p className="text-xs text-content-quaternary mt-2">Press Enter to send, Shift+Enter for new line</p>
        </div>
      )}
    </div>
  );
}
